import React, { useState } from 'react';
import { Button, Input, Space, Form } from 'antd';
import { PlusOutlined } from '@ant-design/icons';

const { TextArea } = Input;

const MAX_COMMENTS = 10;

interface PayrollCommentFormProps {
	onSubmit: (comments: string[]) => void;
	loading?: boolean;
	submitLabel?: string;
	maxComments?: number;
}

const PayrollCommentForm: React.FC<PayrollCommentFormProps> = ({
	onSubmit,
	loading = false,
	submitLabel = 'Submit comments',
	maxComments = MAX_COMMENTS,
}) => {
	const [comments, setComments] = useState<string[]>(['']);
	const [error, setError] = useState<string>('');

	const trimmed = comments.map((c) => c?.trim() ?? '').filter((c) => c.length > 0);

	const handleChange = (index: number, value: string) => {
		const next = [...comments];
		next[index] = value;
		setComments(next);
		setError('');
	};

	const handleAdd = () => {
		if (comments.length >= maxComments) return;
		setComments([...comments, '']);
	};

	const handleRemove = (index: number) => {
		if (comments.length <= 1) return;
		setComments(comments.filter((_, i) => i !== index));
		setError('');
	};

	const handleSubmit = () => {
		if (trimmed.length === 0) {
			setError('Please add at least one comment.');
			return;
		}
		onSubmit(trimmed);
	};

	return (
		<Form layout="vertical" onFinish={handleSubmit}>
			{comments.map((value, index) => (
				<Form.Item
					key={index}
					label={index === 0 ? 'Comment' : `Comment ${index + 1}`}
					required={index === 0}
				>
					<Space.Compact style={{ width: '100%' }}>
						<TextArea
							value={value}
							onChange={(e) => handleChange(index, e.target.value)}
							placeholder="Enter your comment"
							rows={2}
							disabled={loading}
						/>
						{comments.length > 1 && (
							<Button
								type="text"
								danger
								onClick={() => handleRemove(index)}
								disabled={loading}
							>
								Remove
							</Button>
						)}
					</Space.Compact>
				</Form.Item>
			))}
			{error && (
				<Form.Item validateStatus="error" help={error}>
					<span />
				</Form.Item>
			)}
			<Form.Item>
				<Space>
					{comments.length < maxComments && (
						<Button
							type="dashed"
							icon={<PlusOutlined />}
							onClick={handleAdd}
							disabled={loading}
						>
							Add another comment
						</Button>
					)}
					<Button type="primary" htmlType="submit" loading={loading}>
						{submitLabel}
					</Button>
				</Space>
			</Form.Item>
		</Form>
	);
};

export default PayrollCommentForm;
